import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { debug } from '@/utils/debug';
import type { Campaign, CampaignDetail, CampaignFilters } from '@/types/campaign';

interface CampaignStore {
  // State
  campaigns: Campaign[];
  selectedCampaign: CampaignDetail | null;
  filters: CampaignFilters;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
  refreshConfigOnNav: boolean;

  // Actions
  setCampaigns: (campaigns: Campaign[]) => void;
  setSelectedCampaign: (campaign: CampaignDetail | null) => void;
  setFilters: (filters: Partial<CampaignFilters>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setRefreshConfigOnNav: (value: boolean) => void;
  triggerRefreshOnNav: () => void;

  // Computed getters
  getFilteredCampaigns: () => Campaign[];
  getCampaignById: (id: string) => Campaign | undefined;
}

export const useCampaignStore = create<CampaignStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      campaigns: [],
      selectedCampaign: null,
      filters: {
        search: '',
        type: '',
        status: '',
      },
      isLoading: false,
      error: null,
      lastUpdated: null,
      refreshConfigOnNav: false,

      // Actions
      setCampaigns: (campaigns: Campaign[]) => {
        debug.log('CampaignStore: Setting campaigns', campaigns.length);
        set({
          campaigns,
          lastUpdated: Date.now(),
          error: null,
        });
      },

      setSelectedCampaign: (campaign: CampaignDetail | null) => {
        debug.log('CampaignStore: Setting selected campaign', campaign?.campaign_id);
        set({ selectedCampaign: campaign });
      },

      setFilters: (newFilters: Partial<CampaignFilters>) => {
        const currentFilters = get().filters;
        const updatedFilters = { ...currentFilters, ...newFilters };
        debug.log('CampaignStore: Setting filters', updatedFilters);
        set({ filters: updatedFilters });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      setRefreshConfigOnNav: (value: boolean) => {
        debug.log('CampaignStore: Setting refreshConfigOnNav to', value);
        set({ refreshConfigOnNav: value });
      },

      triggerRefreshOnNav: () => {
        debug.log('CampaignStore: Triggering refresh on next navigation');
        set({ refreshConfigOnNav: true });
      },

      // Computed getters
      getFilteredCampaigns: () => {
        const { campaigns, filters } = get();
        
        return campaigns.filter(campaign => {
          // Search filter
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch = 
              campaign.campaign_name.toLowerCase().includes(searchLower) ||
              campaign.campaign_id.toLowerCase().includes(searchLower);
            if (!matchesSearch) return false;
          }

          // Type filter
          if (filters.type && filters.type !== '') {
            if (campaign.campaign_type !== filters.type) return false;
          }

          // Status filter
          if (filters.status && filters.status !== '') {
            if (campaign.status !== filters.status) return false;
          }

          return true;
        });
      },

      getCampaignById: (id: string) => {
        const { campaigns } = get();
        return campaigns.find(campaign => campaign.campaign_id === id);
      },
    }),
    {
      name: 'campaign-store',
    }
  )
);